import type {
  ConnectionTestResult,
  Paginated,
  PennylaneInvoiceListItem,
  PennylaneInvoiceStatus,
  PennylaneManualByIdResult,
  PennylaneManualSendResult,
  PennylaneRunDetail,
  PennylaneRunListItem,
  PennylaneRunResult,
  PennylaneSettingsSummary,
  RunStatus,
  SchedulePresetInput,
} from '@flux/shared';
import { apiFetch } from '@/lib/api/client.js';

/** Couche réseau de l'écran Pennylane (`02` §7). Clés locales à cette fonctionnalité. */
export const pennylaneQueryKeys = {
  all: ['pennylane'] as const,
  settings: () => ['pennylane', 'settings'] as const,
  runs: (params: RunsListParams) => ['pennylane', 'runs', params] as const,
  runDetail: (id: string) => ['pennylane', 'runs', id] as const,
  invoices: (params: InvoicesListParams) => ['pennylane', 'invoices', params] as const,
  manualDay: (date: string) => ['pennylane', 'manual', 'day', date] as const,
};

export function fetchPennylaneSettings(): Promise<PennylaneSettingsSummary> {
  return apiFetch<PennylaneSettingsSummary>('/api/pennylane/settings');
}

export interface PennylaneSettingsFormValues {
  enabled: boolean;
  mode: 'simulation' | 'active';
  apiUrl: string;
  apiKey?: string;
  schedule?: string;
  preset?: SchedulePresetInput;
  catchupDays: number;
  importFlag: { name: string; value: string };
  /** Transporteur des CSV de facture ; `null` = bloc transport vide (`02` §12.4). */
  defaultCarrier: { code: string; label: string } | null;
}

export function updatePennylaneSettings(body: PennylaneSettingsFormValues): Promise<PennylaneSettingsSummary> {
  return apiFetch<PennylaneSettingsSummary>('/api/pennylane/settings', { method: 'PUT', body });
}

export function testPennylaneConnection(): Promise<ConnectionTestResult> {
  return apiFetch<ConnectionTestResult>('/api/pennylane/settings/test-connection', { method: 'POST' });
}

export function runPennylaneNow(simulation: boolean): Promise<PennylaneRunResult> {
  return apiFetch<PennylaneRunResult>('/api/pennylane/run', { method: 'POST', body: { simulation } });
}

export interface RunsListParams {
  page: number;
  pageSize: number;
  sortDir: 'asc' | 'desc';
  sortBy?: string;
  status?: RunStatus[];
}

export function fetchPennylaneRuns(params: RunsListParams): Promise<Paginated<PennylaneRunListItem>> {
  return apiFetch<Paginated<PennylaneRunListItem>>('/api/pennylane/runs', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortDir: params.sortDir,
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
      ...(params.status && params.status.length > 0 ? { status: params.status } : {}),
    },
  });
}

export function fetchPennylaneRunDetail(id: string): Promise<PennylaneRunDetail> {
  return apiFetch<PennylaneRunDetail>(`/api/pennylane/runs/${id}`);
}

export interface InvoicesListParams {
  page: number;
  pageSize: number;
  sortDir: 'asc' | 'desc';
  sortBy?: string;
  status?: PennylaneInvoiceStatus[];
  from?: string;
  to?: string;
  search?: string;
}

export function fetchPennylaneInvoices(params: InvoicesListParams): Promise<Paginated<PennylaneInvoiceListItem>> {
  return apiFetch<Paginated<PennylaneInvoiceListItem>>('/api/pennylane/invoices', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortDir: params.sortDir,
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
      ...(params.status && params.status.length > 0 ? { status: params.status } : {}),
      ...(params.from ? { from: params.from } : {}),
      ...(params.to ? { to: params.to } : {}),
      ...(params.search ? { search: params.search } : {}),
    },
  });
}

export function resendPennylaneInvoice(id: string, confirmResend: boolean): Promise<PennylaneInvoiceListItem> {
  return apiFetch<PennylaneInvoiceListItem>(`/api/pennylane/invoices/${id}/resend`, {
    method: 'POST',
    body: { confirmResend },
  });
}

export function forceSendPennylaneInvoice(id: string): Promise<PennylaneInvoiceListItem> {
  return apiFetch<PennylaneInvoiceListItem>(`/api/pennylane/invoices/${id}/force`, { method: 'POST' });
}

export function ignorePennylaneInvoice(id: string, reason: string): Promise<PennylaneInvoiceListItem> {
  return apiFetch<PennylaneInvoiceListItem>(`/api/pennylane/invoices/${id}/ignore`, {
    method: 'POST',
    body: { reason },
  });
}

export function fetchInvoicesForDay(date: string): Promise<{ items: PennylaneInvoiceListItem[] }> {
  return apiFetch<{ items: PennylaneInvoiceListItem[] }>('/api/pennylane/manual/day', { query: { date } });
}

export function sendInvoicesForDay(
  date: string,
  invoiceIds: string[],
  confirmResend: boolean,
): Promise<PennylaneManualSendResult> {
  return apiFetch<PennylaneManualSendResult>('/api/pennylane/manual/day', {
    method: 'POST',
    body: { date, invoiceIds, confirmResend },
  });
}

export function sendInvoiceByIdOrNumber(
  idOrNumber: string,
  confirmResend: boolean,
): Promise<PennylaneManualByIdResult> {
  return apiFetch<PennylaneManualByIdResult>('/api/pennylane/manual/invoice', {
    method: 'POST',
    body: { idOrNumber, confirmResend },
  });
}
